import React, { useState, useEffect, useMemo } from 'react'
import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Avatar,
  Badge
} from '@heroui/react'
import {
  Menu,
  Sun,
  Moon,
  LogOut,
  User,
  Settings,
  Shield,
  Crown
} from 'lucide-react'
import { useUser } from '../../contexts/UserContext'
import { useNavigate } from 'react-router-dom'
import { useUIStore } from '../../stores/useUIStore'
import { useTheme } from '../../contexts/ThemeProvider'
import NotificationPanel from '../common/NotificationPanel'
import { cn } from '../../utils/cn'
import toast from 'react-hot-toast'
import V2BoardAPI from '../../services/api'
import { getUserInfo } from '../../services/user'

const Header = () => {
  const { user, logout, updateUserData } = useUser()
  const navigate = useNavigate()
  const { toggleSidebar } = useUIStore()
  const { isDark, toggleTheme } = useTheme()

  const [isScrolled, setIsScrolled] = useState(false)

  // Handle Logout
  const handleLogout = async () => {
    try {
      await logout()
      toast.success('已退出登录')
      navigate('/login')
    } catch (error) {
      toast.error('退出登录失败，请重试')
    }
  }

  // Optimized scroll listener with throttle
  useEffect(() => {
    let ticking = false
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 10)
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Fetch user info for avatar if missing
  useEffect(() => {
    const fetchAvatar = async () => {
      if (user?.token && user?.baseUrl && !user?.avatar_url) {
        try {
          const api = new V2BoardAPI(user.baseUrl, user.token)
          const userInfo = await getUserInfo(api)

          if (userInfo?.avatar_url) {
            // 更新 Store
            updateUserData({ avatar_url: userInfo.avatar_url })
          }
        } catch (error) {
          console.error('Failed to fetch user avatar:', error)
        }
      }
    }

    fetchAvatar()
  }, [user?.token, user?.baseUrl, user?.avatar_url, updateUserData])

  // Memoize avatar URL
  const avatarUrl = useMemo(() =>
    user?.avatar_url || `https://i.pravatar.cc/150?u=${user?.email || 'v2board'}`,
    [user?.avatar_url, user?.email]
  )

  return (
    <Navbar
      maxWidth="full"
      height="72px"
      isBordered={isScrolled}
      className={cn(
        "transition-all duration-200",
        isScrolled
          ? "bg-white/95 dark:bg-slate-900/95 backdrop-blur-lg shadow-sm"
          : "bg-white/80 dark:bg-slate-900/80 backdrop-blur-md"
      )}
      classNames={{
        wrapper: "px-4 lg:px-6",
        brand: "gap-4"
      }}
    >
      {/* Left Section */}
      <NavbarContent justify="start" className="gap-3">
        {/* Mobile Menu Toggle */}
        <NavbarItem className="lg:hidden">
          <Button
            isIconOnly
            variant="light"
            size="md"
            onClick={toggleSidebar}
            className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
          >
            <Menu className="w-5 h-5" strokeWidth={2.5} />
          </Button>
        </NavbarItem>
      </NavbarContent>

      {/* Right Section */}
      <NavbarContent justify="end" className="gap-2">
        {/* Theme Toggle */}
        <NavbarItem>
          <Button
            isIconOnly
            variant="light"
            size="md"
            onClick={toggleTheme}
            className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
          >
            {isDark ? (
              <Sun className="w-5 h-5" strokeWidth={2.5} />
            ) : (
              <Moon className="w-5 h-5" strokeWidth={2.5} />
            )}
          </Button>
        </NavbarItem>

        {/* Notifications */}
        <NavbarItem>
          <NotificationPanel />
        </NavbarItem>

        {/* Divider */}
        <div className="h-8 w-px bg-slate-200 dark:bg-slate-700" />

        {/* User Menu */}
        <NavbarItem>
          <Dropdown
            placement="bottom-end"
            classNames={{
              content: "min-w-[280px] p-0 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800"
            }}
          >
            <DropdownTrigger>
              <div className="cursor-pointer">
                <Badge
                  content={<Crown className="w-3 h-3 text-white" strokeWidth={3} />}
                  classNames={{
                    badge: "bg-gradient-to-br from-amber-400 to-orange-500 border-2 border-white dark:border-slate-900"
                  }}
                  placement="bottom-right"
                  size="sm"
                >
                  <Avatar
                    isBordered
                    size="md"
                    src={avatarUrl}
                    classNames={{
                      base: "ring-2 ring-white dark:ring-slate-900"
                    }}
                  />
                </Badge>
              </div>
            </DropdownTrigger>

            <DropdownMenu
              aria-label="User Actions"
              variant="flat"
              classNames={{
                list: "p-0"
              }}
            >
              {/* User Info Section */}
              <DropdownItem
                key="profile-header"
                textValue="User Profile"
                className="h-14 gap-2 opacity-100"
                isReadOnly
              >
                <div className="flex flex-col gap-1">
                  <p className="font-semibold text-sm text-default-600">Signed in as</p>
                  <p className="font-semibold text-sm">{user?.email || 'user@example.com'}</p>
                </div>
              </DropdownItem>

              <DropdownItem
                key="profile"
                textValue="Profile"
                startContent={<User className="w-4 h-4" />}
                onClick={() => navigate('/profile')}
              >
                Profile
              </DropdownItem>
              <DropdownItem
                key="settings"
                textValue="Settings"
                startContent={<Settings className="w-4 h-4" />}
                onClick={() => navigate('/settings')}
              >
                Settings
              </DropdownItem>
              <DropdownItem
                key="help"
                textValue="Help"
                startContent={<Shield className="w-4 h-4" />}
              >
                Help & Support
              </DropdownItem>

              <DropdownItem
                key="logout"
                textValue="Log Out"
                className="text-danger"
                color="danger"
                startContent={<LogOut className="w-4 h-4" />}
                onClick={handleLogout}
              >
                Log Out
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </NavbarItem>
      </NavbarContent>
    </Navbar>
  )
}

export default Header
